import { PageBackLink } from "@/components/page-back-link"
import { ProductDetailSkeleton } from "@/features/products/components/product-detail-skeleton"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { usePortalAuth } from "@/features/authz/hooks/use-portal-auth"
import { useEffect, useState } from "react"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { useQuery } from "@tanstack/react-query"
import { FileText, Pencil } from "lucide-react"

import { ErrorBanner } from "@/components/error-boundary"
import { PageBreadcrumbs } from "@/components/page-header"
import { StatusBadge } from "@/components/status-badge"
import { Button } from "@/components/ui/button"
import { CatalogManagement } from "@/features/products/components/catalog-management"
import { DeleteResourceDialog } from "@/features/products/components/delete-resource-dialog"
import { GEN_HEALTH_CLIENT_PRODUCT_ID_METADATA_KEY, ProductForm } from "@/features/products/components/product-form"
import { useDeleteProduct, useUpdateProduct, type PortalProduct } from "@/features/products/hooks/use-product-mutations"
import { getProductDeleteErrorMessage } from "@/features/products/product-delete-error"
import { api } from "@/lib/api"
import { queryKeys } from "@/lib/query-keys"
import { formatDateTime } from "@/lib/utils"

type ProductDetailResponse = {
	product: PortalProduct
}

export const Route = createFileRoute("/dashboard/products/$productId")({
	component: ProductDetailPage,
})

function ProductDetailPage() {
	const auth = usePortalAuth()
	const canRead = auth.data !== undefined && auth.data.permissions.includes("products.read")
	const canManage = auth.data !== undefined && auth.data.permissions.includes("products.manage")
	const { productId } = Route.useParams()
	const navigate = useNavigate()
	const [isEditing, setIsEditing] = useState(false)
	const [productDraft, setProductDraft] = useState<Partial<PortalProduct> | null>(null)
	const updateProduct = useUpdateProduct(productId)
	const deleteProduct = useDeleteProduct(productId)

	const { data, isLoading, error, refetch } = useQuery({
		enabled: canRead,
		queryKey: queryKeys.products.detail(productId),
		queryFn: () => api.get<ProductDetailResponse>(`/products/${productId}`),
	})

	const product = data?.product

	useEffect(() => {
		if (!product) return
		if (isEditing && productDraft) return
		setProductDraft(product)
	}, [product, isEditing, productDraft])

	if (auth.isPending) return <ProductDetailSkeleton />
	if (auth.isError) return <ErrorBanner title="Unable to load access" message="Your catalog access could not be checked." onRetry={() => void auth.refetch()} />
	if (!canRead) return <ErrorBanner title="Catalog access required" message="You do not have permission to view the product catalog." />

	if (isLoading) {
		return <ProductDetailSkeleton />
	}

	if (error) {
		return (
			<div >
				<ErrorBanner
					title="Failed to load product"
					message={error instanceof Error ? error.message : "Unknown error"}
					onRetry={refetch}
				/>
			</div>
		)
	}

	if (!product) {
		return <div className="text-muted-foreground">Product not found.</div>
	}

	const deleteErrorMessage = getProductDeleteErrorMessage(deleteProduct.error)
	const genHealthClientProductId = product.metadata[GEN_HEALTH_CLIENT_PRODUCT_ID_METADATA_KEY]
	const genHealthClientProductIdLabel =
		typeof genHealthClientProductId === "string" && genHealthClientProductId.length > 0
			? genHealthClientProductId
			: "Not linked"

	return (
		<div className="space-y-7">
			<div className="flex flex-wrap items-center gap-3">
				<PageBreadcrumbs
					items={[
						<Link key="dashboard" to="/dashboard">
							Dashboard
						</Link>,
						<Link key="products" to="/dashboard/products" search={{ page: 1, limit: 25 }}>
							Products
						</Link>,
						<span key="product">{product.name}</span>,
					]}
				/>
			</div>

			<section className="flex flex-col gap-5 py-2 md:flex-row md:items-start md:justify-between">
				<div className="min-w-0">
					<div className="mb-2">
						<span className="text-label font-semibold uppercase tracking-[0.16em] text-brand">
							Product catalog
						</span>
					</div>
					<div className="flex flex-wrap items-center gap-3">
						<PageBackLink to="/dashboard/products" search={{ page: 1, limit: 25 }} label="Back to products" />
						<h1 className="font-avenir text-page-title font-semibold tracking-[-0.035em] text-heading">
							{product.name}
						</h1>
						<StatusBadge variant={product.isActive ? "success" : "secondary"}>
							{product.isActive ? "Active" : "Inactive"}
						</StatusBadge>
					</div>
					<p className="mt-2 text-meta font-medium text-muted-foreground">
						Created {formatDateTime(product.createdAt)} <span className="px-1.5 text-separator">•</span> Updated{" "}
						{formatDateTime(product.updatedAt)}
					</p>
				</div>

				<div className="flex shrink-0 items-center gap-2">
					<DeleteResourceDialog
						triggerLabel="Delete"
						title="Delete product?"
						description="This removes the product and its related catalog records. Products already used by orders cannot be deleted."
						confirmLabel="Delete product"
						isPending={deleteProduct.isPending}
						disabled={!canManage || auth.data === undefined || !auth.data.permissions.includes("orders.read") || updateProduct.isPending}
						errorMessage={deleteErrorMessage}
						onConfirm={() => {
							deleteProduct.mutate(undefined, {
								onSuccess: () => {
									void navigate({
										to: "/dashboard/products",
										search: { page: 1, limit: 25 },
									})
								},
							})
						}}
					/>
				</div>
			</section>

			<div className="space-y-6">
				<section className="rounded-2xl border border-border bg-card shadow-card overflow-hidden">
					<div className="flex items-start justify-between gap-4 border-b border-border-subtle px-6 py-5">
						<div className="flex items-center gap-3">
							<div className="flex size-9 items-center justify-center rounded-xl bg-brand-muted text-brand">
								<FileText className="size-[18px]" strokeWidth={1.8} />
							</div>
							<div>
								<h2 className="text-section-title font-semibold text-heading">Product details</h2>
								<p className="mt-0.5 text-body text-muted-foreground">
									The identifiers and integration link used to manage this product.
								</p>
							</div>
						</div>
						{canManage ? (
							<Dialog open={isEditing} onOpenChange={(open) => {
								if (updateProduct.isPending) return
								setIsEditing(open)
								if (!open) setProductDraft(product)
							}}>
								<DialogTrigger asChild>
									<Button variant="secondary" size="sm" disabled={updateProduct.isPending || deleteProduct.isPending}>
										<Pencil className="size-3.5" />Edit product
									</Button>
								</DialogTrigger>
								<DialogContent className="max-h-[90dvh] overflow-y-auto bg-card sm:max-w-5xl" aria-describedby={undefined}>
									<DialogHeader><DialogTitle>Edit product</DialogTitle></DialogHeader>
									<ProductForm
										mode="edit"
										initialValues={product}
										isPending={updateProduct.isPending}
										submitLabel="Save changes"
										onValuesChange={(next) =>
											setProductDraft((current) => ({
												...(current ?? product),
												...next,
											}))
										}
										onCancel={() => {
											setProductDraft(product)
											setIsEditing(false)
										}}
										onSubmit={async (payload) => {
											await updateProduct.mutateAsync(payload)
											setIsEditing(false)
										}}
									/>
								</DialogContent>
							</Dialog>
						) : null}
					</div>
					<div className="px-6 pb-6 pt-5">
						<div className="grid gap-x-10 gap-y-6 text-body md:grid-cols-2">
							<div>
								<p className="text-label font-semibold uppercase text-subtle-foreground">SKU</p>
								<p className="mt-1 font-medium text-heading">{product.sku ?? "—"}</p>
							</div>
							<div>
								<p className="text-label font-semibold uppercase text-subtle-foreground">
									GenHealth product ID
								</p>
								<p className="mt-1 font-medium text-heading">{genHealthClientProductIdLabel}</p>
							</div>
							{product.description ? (
								<div className="md:col-span-2">
									<p className="text-label font-semibold uppercase text-subtle-foreground">Description</p>
									<p className="mt-1 leading-6 text-foreground-secondary">{product.description}</p>
								</div>
							) : null}
						</div>
					</div>
				</section>

				<CatalogManagement canManage={canManage} product={product} {...(productDraft ? { productDraft } : {})} />
			</div>
		</div>
	)
}
